import { Link, useParams, useSearchParams } from 'react-router-dom';
import { VerifiedBadge } from '../../components/common/Badges';
import { Button } from '../../components/ui/button';
import { ShieldAlert, Trophy, QrCode, Loader2 } from 'lucide-react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { useAuth } from '../../lib/auth-context';
import { ShareResultButton } from '../../components/common/ShareResultButton';
import { formatDuration, computePace } from '../../lib/formatters';

export default function MobileResult() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const status = params.get('status') || 'verified';
  const verified = status === 'verified';

  const { profile } = useAuth();
  const { data: event, loading } = useApi(api.getEvent, [id]);
  const { data: participation } = useApi(api.getMyParticipation, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const finishSec = participation?.finishTimeSec || 0;
  const distanceKm = Number(event?.distanceKm || 0);
  const paceSec = finishSec && distanceKm ? Math.floor(finishSec / distanceKm) : null;
  const timeLabel = finishSec ? formatDuration(finishSec) : '—';
  const distanceLabel = distanceKm ? `${distanceKm}K` : '—';
  const bibLabel = participation?.bibNumber ? `#${participation.bibNumber}` : '—';

  return (
    <div className="px-4 pt-6 pb-6" data-testid={`result-${status}`}>
      <div className={`rivl-card p-6 text-center space-y-4 ${verified ? 'border-brand-blue' : 'border-destructive'}`}>
        <div className={`mx-auto h-16 w-16 rounded-md flex items-center justify-center ${verified ? 'bg-brand-blue/10 text-brand-blue' : 'bg-destructive/10 text-destructive'}`}>
          {verified ? <Trophy className="h-8 w-8" /> : <ShieldAlert className="h-8 w-8" />}
        </div>
        <div>
          <p className="rivl-label">{verified ? 'Verified Finish' : 'Unverified Finish'}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
            {verified ? 'Run confirmed' : 'Could not confirm route'}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
            {verified
              ? 'Your full route, GPS trace, and timing all check out. Your finish is on the leaderboard.'
              : "We couldn't confirm full route completion. Your run is recorded but won't appear on the verified leaderboard."}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2">
          <Stat label="Time" value={timeLabel} />
          <Stat label="Bib" value={bibLabel} />
          <Stat label="Distance" value={distanceLabel} />
        </div>

        {verified && <VerifiedBadge size="lg" className="inline-flex" />}
      </div>

      {verified && event?.medalEnabled && participation?.qrToken && (
        <div className="rivl-card mt-4 p-5 text-center">
          <p className="rivl-label">Medal Token</p>
          <h3 className="font-display text-lg font-semibold mt-1 mb-3">Collect your medal</h3>
          <div className="mx-auto h-44 w-44 bg-foreground text-background rounded-md flex items-center justify-center relative overflow-hidden">
            <QrCode className="h-32 w-32" strokeWidth={1} />
            <div className="absolute inset-0 rivl-noise opacity-20" />
          </div>
          <p className="text-xs text-muted-foreground mt-3 font-mono">{participation.qrToken}</p>
          <p className="text-xs text-muted-foreground mt-1">Show this to a RIVL scanner at the finish line.</p>
        </div>
      )}

      {finishSec > 0 && (
        <div className="mt-4">
          <ShareResultButton
            className="h-11 rounded-md w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            testid="result-share-btn"
            label={verified ? 'Share your verified finish' : 'Share your finish'}
            stats={{
              name: profile?.name || 'A RIVL runner',
              distanceKm,
              durationSec: finishSec,
              paceSecPerKm: paceSec,
              eventName: event?.name,
              verified,
              dateISO: participation?.finishedAt || new Date().toISOString(),
              path: Array.isArray(participation?.gpsTrack) ? participation.gpsTrack : null,
            }}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mt-4">
        <Button asChild variant="outline" className="h-11 rounded-md">
          <Link to="/" data-testid="result-home-btn">Home</Link>
        </Button>
        <Button asChild className="h-11 rounded-md" data-testid="result-leaderboard-btn">
          <Link to={`/events/${id}/leaderboard`}>Leaderboard →</Link>
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-secondary py-2">
      <div className="font-mono font-bold">{value}</div>
      <div className="rivl-label text-[9px]">{label}</div>
    </div>
  );
}
